// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface PolicyType {
  id?: string;
  providerId: string;
  providerName?: string;
  name: string;
  code: string;
  description?: string;
  category: 'INSURANCE' | 'INVESTMENT' | 'BANKING' | 'PENSION';
  defaultTemplateId?: string;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
  overrideEmail?: string;
  emailOverrideReason?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const API_BASE = '/api/policy-types';

export const policyTypeApi = {
  async getAll(params?: {
    providerId?: string;
    category?: string;
    search?: string;
    isActive?: boolean;
    page?: number;
    size?: number;
  }): Promise<PageResponse<PolicyType>> {
    const queryParams = new URLSearchParams();
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch policy types');
    }
    const data = await response.json();
    // Return full paginated response
    return data;
  },

  async getById(id: string): Promise<PolicyType> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch policy type');
    }
    return response.json();
  },

  async create(policyType: PolicyType): Promise<PolicyType> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(policyType)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create policy type');
    }
    return response.json();
  },

  async update(id: string, policyType: PolicyType): Promise<PolicyType> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(policyType)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update policy type');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete policy type');
    }
  }
};
